// Shared alert derivation used by both the notification bell (CalNotifications)
// and the full-page Notification Center, so they always agree. Alerts are derived
// from the same per-meter dashboard data the fleet view uses (the registry is the
// single source of truth): red -> critical, yellow -> warning, plus regression-drift.
//
// IDs are STABLE (keyed by meter_product_id + type, no array index) so read/unread
// state can be persisted across refreshes.

export type CalAlertSeverity = 'critical' | 'warning' | 'info';
export type CalAlertKind = 'health' | 'drift' | 'event';

export interface CalAlert {
  id: string;
  severity: CalAlertSeverity;
  kind: CalAlertKind;
  kindLabel?: string;   // chip label (e.g. "Control limits", "New proving")
  title: string;
  message: string;
  meterId?: string;
  meterProductId?: string;
  recommendation?: string;  // Cal's suggestion (health/drift only)
  timestamp: Date;
  read: boolean;
  server?: boolean;     // true = persisted server notification (read state syncs server-side)
}

// Map persisted server notifications (new provings, verification-needed, failures)
// into the same shape the feed renders.
const SERVER_KIND_LABEL: Record<string, string> = {
  new_proving: 'New proving',
  verification_needed: 'Verification',
  processing_failure: 'Processing',
};
export function mapServerNotifications(list: any[]): CalAlert[] {
  return (list || []).map((n) => ({
    id: n.id,
    severity: (['critical', 'warning', 'info'].includes(n.severity) ? n.severity : 'info') as CalAlertSeverity,
    kind: 'event' as CalAlertKind,
    kindLabel: SERVER_KIND_LABEL[n.type] || 'Event',
    title: n.title || 'Notification',
    message: n.body || '',
    meterProductId: n.meter_product_id || undefined,
    timestamp: n.created_at ? new Date(n.created_at) : new Date(),
    read: Boolean(n.read),
    server: true,
  }));
}

const fmt = (v: any) => (typeof v === 'number' ? v.toFixed(4) : v);

export function deriveAlerts(meters: any[]): CalAlert[] {
  const alerts: CalAlert[] = [];

  // Control-limit health: red -> critical, yellow -> warning.
  meters.forEach((m: any) => {
    const health = m.current_health;
    if (health !== 'red' && health !== 'yellow') return;

    const name = m.product_type ? `${m.meter_id} · ${m.product_type}` : m.meter_id;
    const factor = m.current_meter_factor;
    const cl = m.control_limits || {};
    const reasons = Array.isArray(m.health_reasons) ? m.health_reasons.join('; ') : '';
    const limitsText = cl.lcl != null && cl.ucl != null ? ` (limits ${fmt(cl.lcl)}–${fmt(cl.ucl)})` : '';
    const factorText = factor != null ? `Meter factor ${fmt(factor)}` : 'Meter factor';

    if (health === 'red') {
      alerts.push({
        id: `critical-${m.meter_product_id}`,
        severity: 'critical',
        kind: 'health',
        title: `Critical: ${name}`,
        message: reasons || `${factorText} outside control limits${limitsText}.`,
        meterId: m.meter_id,
        meterProductId: m.meter_product_id,
        recommendation:
          'Cal recommends immediate inspection: verify calibration, check for mechanical issues, and review recent proving history.',
        timestamp: new Date(),
        read: false,
      });
    } else {
      alerts.push({
        id: `warning-${m.meter_product_id}`,
        severity: 'warning',
        kind: 'health',
        title: `Warning: ${name}`,
        message: reasons || `${factorText} approaching control limits${limitsText}.`,
        meterId: m.meter_id,
        meterProductId: m.meter_product_id,
        recommendation:
          'Cal suggests monitoring this meter and scheduling preventive maintenance if the trend continues.',
        timestamp: new Date(),
        read: false,
      });
    }
  });

  // Regression drift: inside control limits but drifting from the forecast band.
  // Trust-gated so we don't cry wolf on a model that can't predict.
  meters.forEach((m: any) => {
    const d = m.regression_drift;
    if (!d) return;
    const trust = d.trust_level;
    if (trust !== 'reliable' && trust !== 'limited') return;
    const cons = d.consecutive_exceptions ?? 0;
    const confirmed = Boolean(d.drift_flag);
    if (!confirmed && cons < 1) return;

    const name = m.product_type ? `${m.meter_id} · ${m.product_type}` : m.meter_id;
    const fText = d.forecast_expected != null ? ` Expected next ≈ ${fmt(d.forecast_expected)}.` : '';
    alerts.push({
      id: `drift-${m.meter_product_id}`,
      severity: confirmed ? 'warning' : 'info',
      kind: 'drift',
      title: `${confirmed ? 'Regression drift' : 'Watch — drifting'}: ${name}`,
      message: `${cons} recent proving${cons === 1 ? '' : 's'} fell outside the regression forecast band.${fText} Forecast trust: ${trust}.`,
      meterId: m.meter_id,
      meterProductId: m.meter_product_id,
      recommendation: confirmed
        ? 'Cal recommends closer attention — recent provings are consistently beating the forecast band, an early drift signal even if control limits are still OK.'
        : 'Cal is watching this meter — a recent proving fell outside the forecast band. Keep an eye on the next one.',
      timestamp: new Date(),
      read: false,
    });
  });

  const rank = (s: string) => (s === 'critical' ? 0 : s === 'warning' ? 1 : 2);
  alerts.sort((a, b) => rank(a.severity) - rank(b.severity));
  return alerts;
}

// --- read/unread persistence (per user, localStorage) ---------------------
const readKey = (email?: string) => `calNotifRead:${email || 'anon'}`;

export function loadReadIds(email?: string): Set<string> {
  try {
    const raw = localStorage.getItem(readKey(email));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

export function saveReadIds(email: string | undefined, ids: Set<string>): void {
  try {
    localStorage.setItem(readKey(email), JSON.stringify(Array.from(ids)));
    // Notify same-tab listeners (the bell) so read items drop off immediately.
    window.dispatchEvent(new Event('cal-notif-read-changed'));
  } catch {
    /* ignore quota / disabled storage */
  }
}
